import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { useHierarchy } from '@/store/hierarchy';
import { useOutPriceConfigs } from '@/store/out-price-configs';
import { MarkupMarginType, type OutPriceValueDto } from '@/ApiClient';
import { marginToMarkup, markupToMargin } from '@/components/prices/utils/price-math';

/** Mirrors MarkupMarginTargetService's constants. */
export const DEFAULT_MARKUP_TARGET = 2.5;
export const DEFAULT_MARGIN_TARGET = 65;

export const useMarkupTargets = defineStore('markupTargets', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.price_GetAllMarkupTargets,
    dto: 'MarkupMarginTargetDto',
    signalR: 'MarkupTargetUpdate',
  });

  /** Self first, then up the ancestor chain. */
  const findNearest = (outPriceId: number, hierarchyNodeId?: number): number | undefined => {
    const hierarchy = useHierarchy();
    const markupTargets = globalStoreBase.loaded.value.filter((t) => t.outPriceId === outPriceId);

    if (hierarchyNodeId) {
      const ancestors = hierarchy.getNodeAndAncestors(hierarchyNodeId);
      for (const node of ancestors) {
        const target = markupTargets.find((t) => t.hierarchyNodeId === node.id);
        if (target) return target.amount;
      }
    }

    // Fallback to root-level target (should always exist)
    const rootNode = hierarchy.getRootNode();
    return markupTargets.find((t) => t.hierarchyNodeId === rootNode?.id)?.amount;
  };

  const getForHierarchy = (outPriceId: number, hierarchyNodeId?: number): number => {
    const outPrice = useOutPriceConfigs().outPriceById[outPriceId];

    const own = findNearest(outPriceId, hierarchyNodeId);
    if (own !== undefined) return own;

    // Every season gets its own cloned out-price rows, so without this a season-scoped out-price
    // never sees the targets configured on the brand's default config. Must stay in step with
    // MarkupMarginTargetService.GetMarkupMarginTarget, which resolves the persisted value.
    const seasonless = outPrice ? useOutPriceConfigs().seasonlessOutPriceWithKey(outPrice.key) : undefined;
    if (seasonless && seasonless.id !== outPriceId) {
      const inherited = findNearest(seasonless.id, hierarchyNodeId);
      if (inherited !== undefined) {
        // Amounts are stored in their own out-price's mode, and the two can differ.
        return getMarkupMarginAs(
          { amount: inherited, type: markupMarginTypeOf(seasonless.preferMarkup) },
          markupMarginTypeOf(outPrice.preferMarkup)
        );
      }
    }

    return outPrice?.preferMarkup === false ? DEFAULT_MARGIN_TARGET : DEFAULT_MARKUP_TARGET;
  };

  const markupMarginTypeOf = (preferMarkup: boolean) =>
    preferMarkup ? MarkupMarginType.Markup : MarkupMarginType.Margin;

  const getMarkupMarginAs = (
    markupMargin: { amount: number; type: MarkupMarginType },
    type: MarkupMarginType
  ): number => {
    if (type === markupMargin.type) {
      return markupMargin.amount;
    }
    if (type === MarkupMarginType.Markup) {
      return marginToMarkup(markupMargin.amount);
    }
    return markupToMargin(markupMargin.amount);
  };

  return { ...globalStoreBase, getForHierarchy, getMarkupMarginAs };
});
